import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
    isEdgeChromium,
    isFirefox,
    isMacOs,
    isSafari,
} from 'react-device-detect';
import { Dialog } from '@bb-ui/react-library/dist/components/Dialog';
import { DialogTitle } from '@bb-ui/react-library/dist/components/DialogTitle';
import { DialogContent } from '@bb-ui/react-library/dist/components/DialogContent';
import { DialogActions } from '@bb-ui/react-library/dist/components/DialogActions';
import { Banner } from '@bb-ui/react-library/dist/components/Banner';
import { DefaultButton } from '@bb-ui/react-library/dist/components/Button';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { Link } from '@bb-ui/react-library/dist/components/Link';
import { CopyButton } from '../../../CopyButton/CopyButton';
import { useDialogTabWrap } from '../../../../hooks/useDialogTabWrap';
import { useStyles } from '../Authentication.styles';
import { useIdpTestLink } from '../IdpTest/idpTestLink';
import { useSpMetadataUrl } from '../spMetadataUrl';
import { TransText } from '../transHelper';
import { useHelpLinks } from '../../../../hooks/useHelpLinks';

interface Props {
    open: boolean;
    onClose: () => void;
    identityProviderName?: string;
    /** Auth0 connection name; no link without it. */
    connectionName?: string;
}

type Browser = 'edge' | 'firefox' | 'safari' | 'chrome';

const currentBrowser = (): Browser => {
    if (isEdgeChromium) return 'edge';
    if (isFirefox) return 'firefox';
    if (isSafari) return 'safari';
    return 'chrome';
};

/** New private window shortcut: N, or P in Firefox; ⇧⌘ on macOS. */
export const privateWindowShortcut = (browser: Browser, mac: boolean) => {
    const key = browser === 'firefox' ? 'P' : 'N';
    return mac ? `⇧⌘${key}` : `Ctrl+Shift+${key}`;
};

const T = 'settings.authentication.test';

/**
 * Gives the admin a test sign-in link to open in a private window.
 *
 * Auth0 keeps one session per browser, and signing in as another user replaces
 * it, so a test in this browser context (tab, popup, iframe) would swap out the
 * admin's session. A private window has separate cookies. Pages can't open one,
 * so the admin does it via the context menu, and plain clicks are intercepted.
 * The result shows in the private window (IdpTestResultPage).
 */
export const TestIdentityProviderDialog: React.FunctionComponent<Props> = ({
    open,
    onClose,
    ...provider
}) => {
    // Keep the last opened provider during the close transition: callers clear
    // it on close, which would flash the "unavailable" banner.
    const shown = React.useRef(provider);
    if (open) shown.current = provider;
    const { identityProviderName, connectionName } = shown.current;

    const { t } = useTranslation();
    const classes = useStyles();
    useDialogTabWrap(open);
    const [warned, setWarned] = React.useState(false);
    const link = useIdpTestLink(connectionName, identityProviderName);
    const spMetadataUrl = useSpMetadataUrl(connectionName);

    const browser = currentBrowser();
    // Safari's context menu has no private-window entry.
    const menuItem =
        browser === 'safari'
            ? ''
            : t(
                  `${T}.menuItem.${browser}${
                      isMacOs && browser === 'chrome' ? 'Mac' : ''
                  }`,
              );
    const click = t(isMacOs ? 'general.rightClickMac' : 'general.rightClick');
    const browserVariant = browser === 'safari' ? 'Safari' : '';
    const shortcut = privateWindowShortcut(browser, isMacOs);
    const name = identityProviderName
        ? t('general.quotedName', { name: identityProviderName })
        : t(`${T}.providerGeneric`);
    const helpLink = useHelpLinks()('contactSupport');

    React.useEffect(() => {
        if (open) {
            setWarned(false);
        }
    }, [open, identityProviderName]);

    // Any click would open the link in this browser session.
    const intercept = (event: React.MouseEvent) => {
        event.preventDefault();
        setWarned(true);
    };
    // Middle button only: Chrome and Edge also fire auxclick for the right button,
    // which must reach the context menu.
    const interceptMiddle = (event: React.MouseEvent) => {
        if (event.button === 1) intercept(event);
    };

    const titleId = 'idp-test-dialog-title';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            classes={{ paper: classes.testPaper }}
            aria-labelledby={titleId}
            data-testid="testIdentityProviderDialog"
        >
            <DialogTitle id={titleId} hideCloseButton>
                {identityProviderName
                    ? t(`${T}.heading`, { name })
                    : t(`${T}.headingGeneric`)}
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
                {!link && (
                    <Banner severity="error" data-testid="test-unavailable">
                        {/* prettier-ignore */}
                        <Trans i18nKey={`${T}.testUnavailable`}>
                            This identity provider can’t be tested right now: some of its details didn’t load. Refresh the page and try again. If it still can’t be tested, contact <a href={helpLink} target="_blank" rel="noreferrer">Blackboard Support</a>.
                        </Trans>
                    </Banner>
                )}
                {link && (
                    <>
                        <Typography variant="body2">
                            {/* prettier-ignore */}
                            <Trans i18nKey={`${T}.intro`} values={{ name }}>
                                {`To test {name}, you sign in with it in a private browser window. That keeps the test separate from this window, so you stay signed in to `}<span lang="en">Illuminate</span> here.
                            </Trans>
                        </Typography>

                        <ol className={classes.testSteps}>
                            <li>
                                <Typography variant="body2">
                                    {t(
                                        `${T}.stepOpenPrivateWindow${browserVariant}`,
                                        {
                                            click,
                                            menuItem,
                                            shortcut,
                                        },
                                    )}
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2">
                                    {/* prettier-ignore */}
                                    <Trans i18nKey={`${T}.stepSignIn`} values={{ name }}>
                                        {`Sign in with any account from {name}, for example a test account. The account doesn’t need to exist in `}<span lang="en">Illuminate</span>.
                                    </Trans>
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body2">
                                    {/* prettier-ignore */}
                                    <Trans i18nKey={`${T}.stepReadResult`}>
                                        The private window then shows the result. If the sign-in worked, you see the username to use for that account in <span lang="en">Illuminate</span>. If it didn’t, you see what went wrong and who needs to fix it.
                                    </Trans>
                                </Typography>
                            </li>
                        </ol>

                        <div className={classes.testLinkRow}>
                            {/* Real href, for the context menu's private-window entry. */}
                            <Link
                                href={link}
                                onClick={intercept}
                                onAuxClick={interceptMiddle}
                                rel="noopener noreferrer"
                                data-testid="idp-test-link"
                            >
                                {t(`${T}.testLinkLabel`, { name })}
                            </Link>
                            {/* Keyed so a new link starts without the old status. */}
                            <CopyButton
                                key={link}
                                text={link}
                                label={t(`${T}.copyLink`)}
                                successMessage={t(`${T}.copyLinkSuccess`)}
                                failureMessage={t(`${T}.copyLinkFailure`, {
                                    click,
                                })}
                                testId="idp-test-copy-link"
                                className={classes.testLinkCopy}
                                statusClassName={classes.testLinkCopyStatus}
                            />
                        </div>

                        {/* Rendered up front so the warning is announced. */}
                        <div role="alert">
                            {warned && (
                                <Banner
                                    severity="warning"
                                    data-testid="idp-test-click-warning"
                                >
                                    <TransText
                                        text={t(
                                            `${T}.clickWarning${browserVariant}`,
                                            { click, menuItem, shortcut },
                                        )}
                                        tags={[
                                            <span key="english" lang="en" />,
                                        ]}
                                    />
                                </Banner>
                            )}
                        </div>

                        <details className={classes.testTroubleshooting}>
                            <Typography component="summary" variant="subtitle2">
                                {t(`${T}.noResultHeading`)}
                            </Typography>
                            <Typography variant="body2">
                                {/* prettier-ignore */}
                                <Trans i18nKey={`${T}.noResultIntro`} values={{ name }}>
                                    {`If the private window shows an error page instead of a test result (for example, an error from {name}, or a “Not found” page), the sign-in didn’t get back to `}<span lang="en">Illuminate</span>. Usually a setting in your identity provider needs fixing. Check that:
                                </Trans>
                            </Typography>
                            <ul className={classes.testSteps}>
                                {[
                                    'noResultAcs',
                                    'noResultEntityId',
                                    'noResultAssigned',
                                    'noResultCertificate',
                                ].map(key => (
                                    <li key={key}>
                                        <Typography variant="body2">
                                            <TransText
                                                text={t(`${T}.${key}`)}
                                                tags={[
                                                    <span
                                                        key="english"
                                                        lang="en"
                                                    />,
                                                ]}
                                            />
                                        </Typography>
                                    </li>
                                ))}
                            </ul>
                            {spMetadataUrl && (
                                <Typography variant="body2">
                                    {t(`${T}.noResultSpMetadataUrlLabel`)}{' '}
                                    <span
                                        className={classes.monoValue}
                                        data-testid="idp-test-sp-metadata-url"
                                    >
                                        {spMetadataUrl}
                                    </span>
                                </Typography>
                            )}
                            <Typography variant="body2">
                                {/* prettier-ignore */}
                                <Trans i18nKey={`${T}.noResultSupport`}>
                                    If all of these are right and the test still shows no result, contact <a href={helpLink} target="_blank" rel="noreferrer">Blackboard Support</a>.
                                </Trans>
                            </Typography>
                        </details>
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <DefaultButton onClick={onClose} data-testid="testClose">
                    {t('general.close')}
                </DefaultButton>
            </DialogActions>
        </Dialog>
    );
};
